/** @jsxImportSource @emotion/react */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useFieldArray, useFormContext } from "react-hook-form";
import { css } from "@emotion/react";
import { useGetNewsList } from "../../../../service/api";
import { PageHeader } from "../../../../components/page-skeleton/PageHeader";
import { InfoBox } from "../../../../components/Infobox";
import { StyledText } from "../../../../assets/typography/typography";
import { colors } from "../../../../components/colors";
import { CardContainer } from "../../../../components/page-skeleton/CardContainer";
import { CardBody } from "../../../../components/page-skeleton/CardBody";
import { cardBottomPadding } from "../../../../style-util";
import { useSelector } from "react-redux";
import * as authSelectors from "../../../auth/authSelectors";
import { SelectInput } from "../../../../components/forms/inputs/SelectInput";
import { DatePickerInput } from "../../../../components/forms/inputs/DatePickerInput";
import { useHandleFetchError } from "../../../../error-handler";
import { ButtonPrimary, DeleteButton } from "../../../../components/NewButtons";
import { DragAndDropContainer } from "../../../../components/DragAndDropContainer";
import { Table } from "../../../../components/table-components/Table";
import { TableRowHeader } from "../../../../components/table-components/TableRowHeader";
import { TableCellHeaderText } from "../../../../components/table-components/TableCellHeaderText";
import { TableRow } from "../../../../components/table-components/TableRow";
import { TableCellText } from "../../../../components/table-components/TableCellText";
import Sortable from "sortablejs";
import { MutableRefObject } from "react";

const infoBoxBodyStyle = css`
  display: flex;
  flex-direction: row;
  text-align: left;
  gap: 16px;
`;

const newsWrapperStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const dragAndDropRowStyle = css`
  cursor: grab;
`;

const dragAndDropContainerStyle = css`
  border-bottom: 1px solid ${colors.greyBorder};
`;

const formCellStyle = css`
  padding: 8px;
  vertical-align: top;
`;

export const AppHomepageNewsSection = () => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const { control, getValues, setValue } = useFormContext();
  const { data: news, error: getNewsListError } = useGetNewsList(
    organizationKeyName
  );

  useHandleFetchError(getNewsListError);

  const { fields, remove, append, move } = useFieldArray({
    name: "announcementsItems",
    control: control,
  });

  const onDrop = (
    event: Sortable.SortableEvent,
    containerRef: MutableRefObject<HTMLElement | null>
  ) => {
    if (
      !containerRef ||
      event.oldIndex === undefined ||
      event.newIndex === undefined
    ) {
      return;
    }

    move(event.oldIndex, event.newIndex);

    const currentItems = getValues("announcementsItems") || [];
    currentItems.forEach((item: any, index: number) => {
      setValue(`announcementsItems.${index}.sortKey`, index + 1);
    });
  };

  if (!news) {
    return null;
  }

  const getFilteredOptions = (index: number) => {
    const selectedNewsId = getValues(
      `announcementsItems.${index}.announcementId` as any
    );

    const selectedNews = news.find((n) => {
      return n.id === selectedNewsId;
    });

    const optionsWithoutSelectedNews = (news || []).filter((x) => {
      return getValues("announcementsItems").every((field: any) => {
        return field.announcementId !== x.id;
      });
    });

    const options = [
      ...optionsWithoutSelectedNews,
      ...(selectedNews ? [selectedNews] : []),
    ].map((x) => ({
      label: x.title,
      value: x.id,
    }));

    options.unshift({
      label: "",
      value: "",
    });
    return options;
  };

  return (
    <CardContainer>
      <PageHeader
        title="News"
        subtitle="Choose news to display on the homepage."
      />
      <CardBody css={cardBottomPadding}>
        <InfoBox backgroundColor={colors.ctaInactive} className="mb-2 p-5">
          <div css={infoBoxBodyStyle}>
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
            >
              <FontAwesomeIcon icon={solid("info-circle")} />
            </StyledText>
            <StyledText
              color={colors.textPrimary}
              className="bodyTextSemibold mb-0"
            >
              Drag and drop the news items to reorder them. They are sorted from
              top to bottom. E.g. the first news in the list below will be shown
              as the first news in the app and so on.
            </StyledText>
          </div>
        </InfoBox>

        {fields.length > 0 && (
          <Table>
            <thead>
              <TableRowHeader>
                <th />
                <TableCellHeaderText>News</TableCellHeaderText>
                <TableCellHeaderText>Valid from</TableCellHeaderText>
                <TableCellHeaderText>Visible to</TableCellHeaderText>
                <th />
              </TableRowHeader>
            </thead>
            <DragAndDropContainer
              isTable={true}
              onEnd={onDrop}
              containerStyle={dragAndDropContainerStyle}
            >
              {fields.map((field, index) => (
                <TableRow
                  key={field.id}
                  data-id={field.id}
                  css={dragAndDropRowStyle}
                >
                  <TableCellText className="pl-5" css={formCellStyle}>
                    <StyledText
                      className="bodyTextRegular mb-0"
                      color={colors.textPrimary}
                    >
                      <FontAwesomeIcon icon={solid("grip-vertical")} />
                    </StyledText>
                  </TableCellText>
                  <TableCellText css={formCellStyle}>
                    <SelectInput
                      name={`announcementsItems.${index}.announcementId`}
                      options={getFilteredOptions(index)}
                    />
                  </TableCellText>
                  <TableCellText css={formCellStyle}>
                    <DatePickerInput
                      name={`announcementsItems.${index}.validFrom`}
                      showTimeSelect
                      placeholder="Select date and time"
                    />
                  </TableCellText>
                  <TableCellText css={formCellStyle}>
                    <DatePickerInput
                      name={`announcementsItems.${index}.validTo`}
                      showTimeSelect
                      placeholder="Select date and time"
                    />
                  </TableCellText>
                  <TableCellText css={formCellStyle}>
                    <DeleteButton onClick={() => remove(index)}>
                      Remove
                    </DeleteButton>
                  </TableCellText>
                </TableRow>
              ))}
            </DragAndDropContainer>
          </Table>
        )}

        <div css={newsWrapperStyle}>
          <ButtonPrimary
            buttonSize="large"
            onClick={() =>
              append({
                announcementId: "",
                validFrom: new Date(),
                validTo: undefined,
                sortKey: fields.length + 1,
              })
            }
            icon={<FontAwesomeIcon icon={solid("plus")} />}
          >
            Add news
          </ButtonPrimary>
        </div>
      </CardBody>
    </CardContainer>
  );
};
